<template>
  <div ref="menu" class="relative">
    <PopperTooltip content="Boards" placement="bottom" hover open-delay="300">
      <button
        class="bg-agentnoon-primary-700 flex h-9 items-center gap-2 truncate overflow-hidden rounded-lg px-2 text-sm font-medium text-white focus:outline-hidden lg:min-w-[280px]"
        @click="isMenuOpen = !isMenuOpen"
      >
        <span>{{ board?.boardName ?? '' }}</span>

        <MainBoardPill v-if="boardId === mainBoardId" class="ml-auto" />

        <ChevronDownIcon
          class="mr-1 h-5 w-5 transition-transform"
          :class="{
            'rotate-180 transform': isMenuOpen,
            'ml-auto': boardId !== mainBoardId
          }"
        />
      </button>
    </PopperTooltip>
    <transition
      enter-active-class="transition ease-out duration-100"
      enter-from-class="transform opacity-0 scale-95"
      enter-to-class="transform opacity-100 scale-100"
      leave-active-class="transition ease-in duration-75"
      leave-from-class="transform opacity-100 scale-100"
      leave-to-class="transform opacity-0 scale-95"
    >
      <div
        v-if="isMenuOpen"
        class="absolute left-0 z-60 mt-2 w-[280px] rounded-lg border bg-white shadow-lg"
      >
        <button
          class="flex w-full items-center justify-between rounded-t-lg border-b px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 active:bg-gray-100"
          @click="navigateToMainBoard()"
        >
          {{ mainBoard?.boardName }}
          <MainBoardPill />
        </button>

        <!-- Search Bar -->
        <div v-if="navigationConfig[FEATURES.Scenarios]?.show" class="p-2">
          <div class="relative">
            <input
              ref="filterInput"
              v-model="searchQuery"
              class="focus:ring-agentnoon-primary-500 focus:border-agentnoon-primary-700 w-full rounded-lg border border-gray-300 py-2 pr-3 pl-10 text-sm font-normal text-gray-900 placeholder:text-gray-500 focus:ring-1 focus:outline-none"
              type="search"
              placeholder="Search"
            />
            <SearchIcon class="absolute top-1/2 left-3 h-5 w-5 -translate-y-1/2 text-gray-500" />
          </div>
        </div>

        <!-- Snapshots Section -->
        <div
          v-if="snapshotScenarios && snapshotScenarios.length > 0"
          class="group/menu relative cursor-pointer"
          @mouseenter="showSnapshotsDropdown = true"
          @mouseleave="showSnapshotsDropdown = false"
        >
          <div class="flex w-full items-center justify-between rounded-lg p-3 hover:bg-gray-50/75">
            <div class="flex items-center gap-2">
              <span class="group-hover/menu:text-bluedark-600 text-sm font-medium text-gray-900">
                Snapshots
              </span>
            </div>
            <ChevronRightIcon class="group-hover/menu:text-bluedark-600 h-5 w-5 text-gray-400" />
          </div>

          <!-- Safe area for mouse movement -->
          <div
            class="absolute top-0 -right-2 hidden h-full w-2 group-hover/menu:block"
            @mouseenter="showSnapshotsDropdown = true"
          />

          <transition
            enter-active-class="transition ease-out duration-100"
            enter-from-class="transform opacity-0 scale-95"
            enter-to-class="transform opacity-100 scale-100"
            leave-active-class="transition ease-in duration-75"
            leave-from-class="transform opacity-100 scale-100"
            leave-to-class="transform opacity-0 scale-95"
          >
            <!-- Right Panel (Nested Dropdown) -->
            <div
              v-if="showSnapshotsDropdown"
              class="absolute top-0 -right-[280px] w-[280px] origin-top-left overflow-hidden rounded-lg border bg-white shadow-lg"
              @mouseenter="showSnapshotsDropdown = true"
            >
              <div class="max-h-[420px] overflow-y-auto">
                <button
                  v-for="(scenario, index) in snapshotScenarios"
                  :key="index"
                  class="group flex w-full items-center justify-between px-4 py-3 text-left transition-colors hover:bg-gray-50"
                  @click="navigateToPlan(scenario.boardId)"
                >
                  <div class="flex w-full items-center justify-between text-sm">
                    <div class="flex-1">
                      <span class="text-gray-900 group-hover:hidden">
                        {{ scenario.boardName }}
                      </span>
                      <span class="text-bluedark-600 hidden group-hover:block">
                        {{ scenario.boardName }} - {{ formatDate(scenario.createdAt) }}
                      </span>
                    </div>
                    <div class="flex items-center gap-3">
                      <span
                        class="rounded-md bg-gray-50 px-3 py-1 text-sm text-gray-500 group-hover:hidden"
                      >
                        {{ formatDate(scenario.createdAt) }}
                      </span>
                      <button
                        class="bg-bluedark-50 text-bluedark-600 hidden rounded-md px-4 py-1 text-sm font-medium group-hover:block"
                      >
                        View
                      </button>
                    </div>
                  </div>
                </button>
              </div>
            </div>
          </transition>
        </div>

        <!-- Scenarios List -->
        <div
          v-if="navigationConfig[FEATURES.Scenarios]?.show"
          data-test="scenarios-list"
          class="max-h-[50vh] overflow-y-auto pb-2"
        >
          <button
            v-for="(scenario, index) in filteredScenarios"
            :key="index"
            data-test="scenario-list-item"
            class="hover:text-bluedark-600 w-full rounded-lg p-3 text-left text-sm font-medium text-gray-900 transition-colors hover:bg-gray-50 active:bg-gray-100"
            @click="navigateToPlan(scenario.boardId)"
          >
            {{ scenario.boardName }}
          </button>
        </div>

        <!-- See All Button -->
        <router-link
          :to="{
            name: 'Landing',
            params: { boardId: mainBoardId }
          }"
          data-test="scenarios-see-all-button"
          class="bg-bluedark-50 text-agentnoon-primary-700 hover:bg-bluedark-50/75 active:bg-bluedark-100 mx-2 mb-2 block rounded-md px-4 py-2.5 text-center font-medium transition-colors"
          @click="isMenuOpen = false"
        >
          See all
        </router-link>
      </div>
    </transition>
  </div>
</template>

<script setup>
import useBoard from '@/hooks/use-board'
import { useStore } from 'vuex'
import { computed, ref, onMounted, onUnmounted } from 'vue'
import { ChevronRightIcon } from '@heroicons/vue/outline'
import ChevronDownIcon from '@/assets/untitled-ui/chevron-down.svg'
import { onClickOutside, useMagicKeys } from '@vueuse/core'
import { useRouter } from 'vue-router'
import SearchIcon from '@/assets/untitled-ui/search.svg'
import MainBoardPill from '@/components/MainBoardPill.vue'
import useNavigation from '@/hooks/use-navigation.js'
import { FEATURES } from '@/lib/Features'

const props = defineProps({
  boardId: {
    type: String,
    required: true
  }
})

const menu = ref(null)
const isMenuOpen = ref(false)
const searchQuery = ref('')
const showSnapshotsDropdown = ref(false)
const windowHeight = ref(window.innerHeight)

const handleResize = () => {
  windowHeight.value = window.innerHeight
}

onMounted(() => {
  window.addEventListener('resize', handleResize)
})

onUnmounted(() => {
  window.removeEventListener('resize', handleResize)
})

useMagicKeys({
  passive: false,
  onEventFired(e) {
    if (e.key === 'Escape' && e.type === 'keydown') {
      isMenuOpen.value = false
      searchQuery.value = ''
    }
  }
})

const { getters } = useStore()
const { boardId: mainBoardId } = useBoard()
const { push } = useRouter()
const { navigationConfig } = useNavigation()

const board = computed(() => getters.getBoard(props.boardId))
const mainBoard = computed(() => getters.getBoard(mainBoardId.value))

const scenarios = computed(() => getters.scenarios(mainBoardId.value))

const navigateToMainBoard = async () => {
  await push({
    name: 'Board',
    params: { boardId: mainBoardId.value }
  })
}

const filterBySearch = (scenarios) => {
  if (!searchQuery.value) return scenarios
  return scenarios.filter((scenario) =>
    scenario.boardName.toLowerCase().includes(searchQuery.value.toLowerCase())
  )
}

const snapshotScenarios = computed(() => {
  const snapshots = scenarios.value.filter((scenario) => scenario.isSnapshot)
  const filtered = filterBySearch(snapshots)
  return filtered.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
})

const filteredScenarios = computed(() => {
  const nonSnapshots = scenarios.value.filter((scenario) => !scenario.isSnapshot)
  const filtered = filterBySearch(nonSnapshots)
  return filtered.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
})

const formatDate = (date) => {
  if (!date) return ''
  return new Date(date)
    .toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric'
    })
    .replace(/\//g, '-')
}

const navigateToPlan = (planId) => {
  push({
    name: 'Plan View',
    params: { boardId: mainBoardId.value, planId }
  })
}

onClickOutside(menu, () => {
  isMenuOpen.value = false
  searchQuery.value = ''
  showSnapshotsDropdown.value = false
})
</script>

<style scoped>
.etihad-color {
  background-color: #cda75a;
  color: white;
}
</style>
